import get from 'lodash/get';

import type { VideoDescriptor } from '../VideoDescriptor';
import { Override } from '@libs/waymark-video/video-descriptor-types';
import {
  CustomEventTarget,
  matchObservableObjectChange,
  getObservableObjectKeyPath,
} from '@libs/util-ts';

export abstract class BaseReference<
  TOverrideType extends Override,
  TValueType = TOverrideType['__activeValue'],
> extends CustomEventTarget<{
  'change:value': TValueType;
}> {
  rawReferenceData: TOverrideType;
  videoDescriptor: VideoDescriptor;

  constructor(referenceData: TOverrideType, videoDescriptor: VideoDescriptor) {
    super();

    this.rawReferenceData = referenceData;
    this.videoDescriptor = videoDescriptor;

    this.videoDescriptor.addEventListener('change', (event) => {
      const isDataValid = this.revalidateRawData();
      if (!isDataValid) {
        return;
      }

      const referenceKeyPath = getObservableObjectKeyPath(this.rawReferenceData);
      if (!referenceKeyPath) {
        return;
      }

      if (matchObservableObjectChange(event.detail, `${referenceKeyPath}.__activeValue`)) {
        this.dispatchEvent('change:value', this.rawReferenceData.__activeValue as TValueType);
      }
    });
  }

  /**
   * Checks that this.rawReferenceData is still pointing to the latest correct object in the video descriptor.
   * Disconnections can occur if the video descriptor is mass updated via something like VideoDescriptor.applyRawVideoDescriptorData()
   *
   * Returns true if the reference data is valid or has been reconnected, false if it has been disconnected and should be cleaned up/ignored.
   */
  private revalidateRawData(): boolean {
    const referenceKeyPath = getObservableObjectKeyPath(this.rawReferenceData);
    if (!referenceKeyPath) {
      return false;
    }

    let foundReferenceData = get(this.videoDescriptor.rawData, referenceKeyPath);

    if (foundReferenceData === this.rawReferenceData) {
      return true;
    }

    if (
      foundReferenceData &&
      foundReferenceData.id === this.rawReferenceData.id &&
      foundReferenceData.type === this.rawReferenceData.type
    ) {
      this.rawReferenceData = foundReferenceData;
      return true;
    }

    foundReferenceData = this.videoDescriptor.rawData.templateManifest.overrides?.find(
      (override) =>
        override.id === this.rawReferenceData.id && override.type === this.rawReferenceData.type,
    );

    if (foundReferenceData) {
      this.rawReferenceData = foundReferenceData;
      return true;
    }

    console.error(
      `Reference data for ${this.rawReferenceData.type} with ID ${this.rawReferenceData.id} not found in video descriptor.`,
    );
    return false;
  }

  getUUID() {
    return this.getID();
  }

  getID() {
    return this.rawReferenceData.id;
  }

  getLabel() {
    return this.rawReferenceData.name;
  }

  getIdealDisplayFrame(): number {
    return this.rawReferenceData.frameNumber ?? 0;
  }

  getIdealDisplayTime() {
    return this.getIdealDisplayFrame() / this.videoDescriptor.getFramerate();
  }

  abstract toString(): string;
}
